import {
  formatBookingDateTime,
  formatDuration,
  formatPrice,
} from '@/helper/formatter'
import { shouldHideFreeLabel } from '@/helper/utils'
import { Order, OrderItem, OrderStatus } from '@/types'
import React, { useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import { Button } from '../ui/button'
import { ExternalLink, Trash2 } from 'lucide-react'
import useCart from '@/contexts/useCart'
import OrderStatusBadge from '../bookings/status-badge'
import Link from 'next/link'
import CroppedCloudinaryImage from '../experiences/cropped-cloudinary-image'

type ComponentGroup = {
  componentOptionId: number
  label: string
  startsAt: string | null
  displayTime: string | null
  items: OrderItem[]
}

const CheckoutItem = ({
  order,
  removable = false,
  displayBoUrl = false,
}: {
  order: Order
  removable?: boolean
  displayBoUrl?: boolean
}) => {
  const { t } = useTranslation('experience')
  const { removeOrder } = useCart()

  const hideFreeLabelForItem = useMemo(
    () =>
      order.amount < 0.01 && shouldHideFreeLabel(order.option?.prices ?? []),
    [order.amount, order.option?.prices]
  )

  const isCombo = order.option?.isCombo
  const componentGroups = useMemo(() => {
    if (!isCombo) return null
    const hasComponentItems = order.items.some((i) => i.componentOptionId)
    if (!hasComponentItems) return null

    const groups = new Map<number, ComponentGroup>()
    for (const item of order.items) {
      const cid = item.componentOptionId
      if (!cid) continue
      if (!groups.has(cid)) {
        const comp = order.option?.componentOptions?.find(
          (c) => c.optionId === cid
        )
        const label =
          comp?.label || item.componentOptionName || order.optionName
        const startsAt = item.reservations?.[0]?.startAt ?? null
        const displayTime = item.displayTime ?? null
        groups.set(cid, {
          componentOptionId: cid,
          label,
          startsAt,
          displayTime,
          items: [],
        })
      }
      groups.get(cid)!.items.push(item)
    }
    return Array.from(groups.values())
  }, [isCombo, order.items, order.option, order.optionName])

  return (
    <div className="space-y-2">
      <div className="flex">
        <div>
          <CroppedCloudinaryImage
            src={order.experience.images?.[0]}
            width={100}
            height={100}
            alt={order.experience.name}
            className="rounded-md aspect-square"
          />
        </div>

        <div className="space-y-1 pl-4 text-sm w-full">
          <h3 className="mb-4 flex w-full justify-between">
            <div className="flex flex-col space-y-1">
              <div className="font-semibold flex space-x-2">
                <div>
                  <div className="">
                    {order.createdVoucher ? (
                      <span>
                        {t('voucher_for', { name: order.experience.name })}
                      </span>
                    ) : (
                      order.experience.name
                    )}
                  </div>

                  {order.createdVoucher ? (
                    <span className="text-xs text-orange-400">
                      {t('redeem_at_desk')}
                    </span>
                  ) : null}
                </div>
                <span className="text-xs">
                  {order.status !== OrderStatus.Cart && (
                    <OrderStatusBadge order={order} />
                  )}
                </span>
              </div>
              {removable && !hideFreeLabelForItem && (
                <span>
                  {order.amount < 0.01
                    ? t('common:free')
                    : formatPrice(order.amount)}
                </span>
              )}
            </div>

            <div className="flex items-center">
              {removable ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  onClick={() => removeOrder(order.id)}
                >
                  <Trash2 />
                </Button>
              ) : !hideFreeLabelForItem ? (
                <span>
                  {order.amount < 0.01
                    ? t('common:free')
                    : formatPrice(order.amount)}
                </span>
              ) : null}

              {displayBoUrl && (
                <Button variant="link" size="icon" className="rounded-full">
                  <Link href={`/bookings/${order.id}`} target="_blank">
                    <ExternalLink />
                  </Link>
                </Button>
              )}
            </div>
          </h3>

          {!order.createdVoucher && !componentGroups && order.startsAt && (
            <div className="text-xs mb-2">
              <strong>{t('starts_at')}:</strong>{' '}
              {formatBookingDateTime(order.startsAt, order.displayTime)}
            </div>
          )}

          {componentGroups && (
            <div className="text-xs mb-2 space-y-0.5">
              {componentGroups.map((group) => (
                <div key={group.componentOptionId}>
                  <strong>{group.label}:</strong>{' '}
                  {group.startsAt
                    ? formatBookingDateTime(group.startsAt, group.displayTime)
                    : '-'}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2 text-xs">
            {!order.createdVoucher && !componentGroups && order.endsAt && (
              <div className="flex space-x-2 justify-between">
                <strong>{t('duration')}:</strong>
                <span>{formatDuration(order.startsAt, order.endsAt)}</span>
              </div>
            )}

            <div className="flex space-x-2 justify-between">
              <strong>{t('option')}:</strong>
              <span>{order.optionName}</span>
            </div>

            {componentGroups ? (
              <div className="space-y-2">
                {componentGroups.map((group) => (
                  <div key={group.componentOptionId}>
                    <div className="font-semibold">{group.label}</div>
                    {group.items.map((item) => (
                      <div key={item.id}>
                        {item.name ?? t('item')} x {item.quantity}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex space-x-2 justify-between">
                <strong>{t('units')}:</strong>
                <div className="text-right">
                  {order.items.map((item) => (
                    <div key={item.id}>
                      {item.name ?? t('item')} x {item.quantity}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {order.pickupPoint && (
              <div className="flex space-x-2 justify-between">
                <strong>{t('meeting_and_pickup')}:</strong>
                <span className="text-right">{order.pickupPoint.formattedAddress}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CheckoutItem
